import { useNavigate } from "react-router-dom";
import CrmSearchSelect from "@/components/crm/CrmSearchSelect";
import type { UpcomingGigFormState } from "@/types/manager";

type ContactSelection = { id: number; label: string };

type ShowFinanceDrawerProps = {
  open: boolean;
  title?: string;
  subtitle?: string;
  values: UpcomingGigFormState;
  onValueChange: (field: keyof UpcomingGigFormState, value: string | number | null) => void;
  onVenueSelect: (selection: ContactSelection | null) => void;
  onContactSelect: (selection: ContactSelection | null) => void;
  onSubmit: () => void;
  onClose: () => void;
  saving?: boolean;
  disabled?: boolean;
};

export function ShowFinanceDrawer({
  open,
  title = "Edit show details",
  subtitle,
  values,
  onValueChange,
  onVenueSelect,
  onContactSelect,
  onSubmit,
  onClose,
  saving = false,
  disabled = false,
}: ShowFinanceDrawerProps) {
  const navigate = useNavigate();
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSubmit();
  };

  const overlayClasses = open ? "opacity-100" : "pointer-events-none opacity-0";
  const panelClasses = open ? "translate-x-0" : "translate-x-full";

  const handleViewLedger = () => {
    if (!values.ledger_entry_id) return;
    navigate(`/accounting?tab=ledger&entry=${encodeURIComponent(values.ledger_entry_id)}`);
    onClose();
  };

  return (
    <div className={`fixed inset-0 z-50 transition ${open ? "visible" : "invisible"}`} aria-hidden={!open}>
      <div
        className={`absolute inset-0 bg-black/60 transition-opacity ${overlayClasses}`}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className={`absolute right-0 top-0 h-full w-full max-w-xl transform bg-background/95 text-foreground/80 shadow-2xl transition-transform ${panelClasses}`}
      >
        <header className="flex items-center justify-between border-b border-border px-6 py-4">
          <div>
            <p className="text-xs uppercase tracking-wide text-amber-300/70">{subtitle ?? "Calendar coordination"}</p>
            <h2 className="text-xl font-semibold text-foreground">{title}</h2>
            {values.show_id && (
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Show ID · {values.show_id}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-border px-3 py-1 text-xs uppercase tracking-wide text-muted-foreground hover:text-foreground"
          >
            Close
          </button>
        </header>

        <form className="flex h-[calc(100%-4rem)] flex-col gap-4 overflow-y-auto px-6 py-5" onSubmit={handleSubmit}>
          {values.ledger_entry_id && (
            <div className="rounded-lg border border-border bg-background/30 p-3 text-xs text-muted-foreground">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Ledger entry</p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <code className="rounded bg-background/40 px-2 py-1 text-primary-foreground dark:text-amber-200">{values.ledger_entry_id}</code>
                <button
                  type="button"
                  className="text-[11px] uppercase tracking-wide text-muted-foreground hover:text-primary-foreground dark:text-amber-200"
                  onClick={() => void navigator.clipboard?.writeText(values.ledger_entry_id ?? "")}
                >
                  Copy
                </button>
                <button
                  type="button"
                  className="text-[11px] uppercase tracking-wide text-amber-300 hover:text-primary-foreground dark:text-amber-100"
                  onClick={handleViewLedger}
                >
                  View in ledger
                </button>
              </div>
            </div>
          )}
          <section className="grid gap-4 md:grid-cols-2">
            <label className="text-xs uppercase tracking-wide text-muted-foreground">
              Date
              <input
                type="date"
                value={values.event_date}
                onChange={(event) => onValueChange("event_date", event.target.value)}
                disabled={disabled || saving}
                className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
                required
              />
            </label>
            <label className="text-xs uppercase tracking-wide text-muted-foreground">
              Time
              <input
                type="time"
                value={values.event_time}
                onChange={(event) => onValueChange("event_time", event.target.value)}
                disabled={disabled || saving}
                className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
              />
            </label>
          </section>
          <section className="grid gap-4 md:grid-cols-2">
            <label className="text-xs uppercase tracking-wide text-muted-foreground">
              City
              <input
                type="text"
                value={values.city}
                onChange={(event) => onValueChange("city", event.target.value)}
                disabled={disabled || saving}
                className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
                placeholder="Nashville"
              />
            </label>
            <label className="text-xs uppercase tracking-wide text-muted-foreground">
              State / Region
              <input
                type="text"
                value={values.state}
                onChange={(event) => onValueChange("state", event.target.value)}
                disabled={disabled || saving}
                className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
                placeholder="TN"
              />
            </label>
          </section>
          <label className="text-xs uppercase tracking-wide text-muted-foreground">
            Venue name
            <input
              type="text"
              value={values.venue}
              onChange={(event) => onValueChange("venue", event.target.value)}
              disabled={disabled || saving}
              className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
              placeholder="Ryman Auditorium"
              required
            />
          </label>
          <CrmSearchSelect
            label="CRM Venue"
            selectedId={values.venue_contact_id ?? null}
            selectedLabel={values.venue_contact_label ?? ""}
            onSelect={onVenueSelect}
            placeholder="Search venue contacts"
            helperText="Link the venue record so CRM + Accounting stay in sync."
            contactType="Venue"
          />
          <CrmSearchSelect
            label="Primary Contact"
            selectedId={values.contact_id ?? null}
            selectedLabel={values.contact_label ?? ""}
            onSelect={onContactSelect}
            placeholder="Search bookers or promoters"
            helperText="Tie the deal sheet to a CRM contact."
          />
          <section className="grid gap-4 md:grid-cols-2">
            <label className="text-xs uppercase tracking-wide text-muted-foreground">
              Guarantee
              <input
                type="text"
                value={values.guarantee}
                onChange={(event) => onValueChange("guarantee", event.target.value)}
                disabled={disabled || saving}
                className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
                placeholder="$5,000"
              />
            </label>
            <label className="text-xs uppercase tracking-wide text-muted-foreground">
              Split terms
              <input
                type="text"
                value={values.split}
                onChange={(event) => onValueChange("split", event.target.value)}
                disabled={disabled || saving}
                className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
                placeholder="85/15 after expenses"
              />
            </label>
          </section>
          <section className="grid gap-4 md:grid-cols-3">
            <label className="text-xs uppercase tracking-wide text-muted-foreground">
              Percent to artist
              <input
                type="text"
                value={values.artist_percentage}
                onChange={(event) => onValueChange("artist_percentage", event.target.value)}
                disabled={disabled || saving}
                className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
                placeholder="85%"
              />
            </label>
            <label className="text-xs uppercase tracking-wide text-muted-foreground">
              House expense
              <input
                type="text"
                value={values.house}
                onChange={(event) => onValueChange("house", event.target.value)}
                disabled={disabled || saving}
                className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
                placeholder="$1,200"
              />
            </label>
            <label className="text-xs uppercase tracking-wide text-muted-foreground">
              Sound expense
              <input
                type="text"
                value={values.sound}
                onChange={(event) => onValueChange("sound", event.target.value)}
                disabled={disabled || saving}
                className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
                placeholder="$400"
              />
            </label>
          </section>
          <section className="grid gap-4 md:grid-cols-3">
            <label className="text-xs uppercase tracking-wide text-muted-foreground">
              Ticket gross
              <input
                type="text"
                value={values.ticket_gross}
                onChange={(event) => onValueChange("ticket_gross", event.target.value)}
                disabled={disabled || saving}
                className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
                placeholder="$8,400"
              />
            </label>
            <label className="text-xs uppercase tracking-wide text-muted-foreground">
              Merch revenue
              <input
                type="text"
                value={values.merch_revenue}
                onChange={(event) => onValueChange("merch_revenue", event.target.value)}
                disabled={disabled || saving}
                className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
                placeholder="$1,250"
              />
            </label>
            <label className="text-xs uppercase tracking-wide text-muted-foreground">
              Tickets sold
              <input
                type="number"
                value={values.attendance}
                onChange={(event) => onValueChange("attendance", event.target.value)}
                disabled={disabled || saving}
                className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
                placeholder="120"
                min={0}
              />
            </label>
          </section>
          <label className="text-xs uppercase tracking-wide text-muted-foreground">
            Notes
            <textarea
              rows={3}
              value={values.notes}
              onChange={(event) => onValueChange("notes", event.target.value)}
              disabled={disabled || saving}
              className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
              placeholder="Add hold details, production notes, or context."
            />
          </label>
          <label className="text-xs uppercase tracking-wide text-muted-foreground">
            Marketing allocation
            <input
              type="text"
              value={values.marketing}
              onChange={(event) => onValueChange("marketing", event.target.value)}
              disabled={disabled || saving}
              className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
              placeholder="$500"
            />
          </label>
          <div className="mt-auto flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
              Syncs calendar ↔ upcoming shows ↔ accounting cash flow
            </p>
            <button
              type="submit"
              className="rounded-lg border border-gold/60 bg-gold/10 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gold transition hover:border-gold disabled:opacity-60"
              disabled={disabled || saving}
            >
              {saving ? "Saving…" : "Save show"}
            </button>
          </div>
        </form>
      </aside>
    </div>
  );
}
